import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { getUserInfo } from 'Utils';
import { getUserSupportTickets } from 'Services/support';
import SupportTicket from 'Components/profile/SupportTicket';
import SupportTicketSkeleton from 'Components/profile/SupportTicketSkeleton';
import NoProfileData from 'Components/profile/NoProfileData';
const CONSUMER_REQUEST_URL = process.env.REACT_APP_CONSUMER_REQUEST_URL;
const MIN_SKELETON_ITEMS = 10;

const Support = ({ country, dispatchToast, language, uim }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [itemMap, setItemMap] = useState({});
  const [ticketCount, setTicketCount] = useState(0);
  const nodeRef = useRef(null);

  const getTickets = async () => {
    try {
      const { userId, userToken } = await getUserInfo(uim);
      const {
        expectedTicketCount,
        expectedTotalCount,
        ticketCount,
        tickets,
      } = await getUserSupportTickets({ userId, userToken });

      tickets.forEach((item) => {
        if (!itemList.includes(item.id)) itemList.push(item.id);
        itemMap[item.id] = item;
      });

      setIsLoading(false);
      setHasError(false);
      setItemList(itemList);
      setItemMap(itemMap);
      setTicketCount(ticketCount);
    } catch ({ message }) {
      console.error(`Error fetching support tickets: ${message}`);
      dispatchToast({
        message: 'Error fetching support tickets, please try again.',
      });
      setIsLoading(false);
      setHasError(true);
    }
  };

  const renderTickets = () => {
    const tickets =
      isLoading || hasError
        ? [...new Array(MIN_SKELETON_ITEMS)].map((item, idx) => (
            <SupportTicketSkeleton key={idx} />
          ))
        : itemList.map((id) => <SupportTicket key={id} {...itemMap[id]} />);

    return (
      <>
        <div className="row__label">Support Tickets</div>
        <div className="support-tickets">{tickets}</div>
      </>
    );
  };

  const renderView = () => {
    if (!isLoading && !hasError && !ticketCount)
      return (
        <CSSTransition
          classNames="fade"
          appear
          in
          key="noSupportData"
          nodeRef={nodeRef}
          timeout={500}
        >
          <div ref={nodeRef}>
            <NoProfileData
              body="When you create and submit a support ticket while signed into
          your Red Bull Account, that ticket’s status will always be
          available for you to access here."
              heading="You have no support tickets created at this time."
            />
          </div>
        </CSSTransition>
      );

    return renderTickets();
  };

  const NewSupportTicketButton = () => (
    <a
      className="button btn--short"
      href={`${CONSUMER_REQUEST_URL}/${`${country}-${language}`.toLowerCase()}/energydrink/contact/assistant`}
    >
      create ticket
    </a>
  );

  useEffect(() => {
    getTickets();
  }, []);

  return (
    <div className="support">
      <div className="sidebar-view__header desktop__header">
        <h1 className="sidebar-view__heading">Support Tickets</h1>
        <NewSupportTicketButton />
      </div>
      <div className="sidebar-view__inner">
        {renderView()}
        <NewSupportTicketButton />
      </div>
    </div>
  );
};

Support.propTypes = {
  country: PropTypes.string.isRequired,
  dispatchToast: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  uim: PropTypes.object.isRequired,
};

Support.defaultProps = {};

export default Support;
